import { useState, type ReactNode } from 'react';
import {
  IconBell, IconChecklist, IconChevronDown, IconChevronLeft, IconChevronRight, IconCopy, IconDots, IconFileText, IconHistory, IconInfoCircle,
  IconLink, IconListDetails, IconMessageCircle, IconRoute, IconShieldCheck, IconUserShield,
} from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { buildDetail, findDetail, type DChip, type DetailKind } from './taskDetailData';
import { useT } from '@hana/i18n';
import './i18n';
import s from './hrProfile.module.css';
import d from './hrDecisionDetail.module.css';
import t from './taskDetail.module.css';

// Mint-Tasks · page=detail (design-specs/Mint-Tasks.md + code board khối D21-NB). Chỉ đọc; nút ghi báo toast. Phiếu không mang số tiền.

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const CHIP: Record<DChip['tone'], string> = { err: t.cErr ?? '', ok: t.cOk ?? '', warn: t.cWarn ?? '', pri: t.cPri ?? '', mute: t.cMute ?? '', code: t.cPri ?? '' };
const GATE = { err: t.gErr ?? '', warn: t.gWarn ?? '', pri: t.gPri ?? '' };
const ini = (u: string) => u.slice(0, 2).toUpperCase();
const TAB_OF: Record<DetailKind, string> = { task: 'mine', ap: 'appr', del: 'deleg', done: 'done' };

function Card({ title, tip, icon, extra, open, onToggle, children }: {
  title: string; tip: string; icon: ReactNode; extra?: ReactNode; open: boolean; onToggle: () => void; children: ReactNode;
}) {
  return (
    <section className={s.card} aria-label={title}>
      <div className={s.hd}>
        <span className={s.hdIc} aria-hidden="true">{icon}</span>
        <h2>{title}</h2>
        <span className={s.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>
        {extra}
        <div className={s.tools}>
          <button type="button" className={s.tog} aria-expanded={open} aria-label={`Mở hoặc gập ${title}`} onClick={onToggle}>
            <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />
          </button>
        </div>
      </div>
      {open && <div className={s.body}>{children}</div>}
    </section>
  );
}

export function TaskDetail() {
  const tr = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { kind = 'ap', id = '101' } = useParams({ strict: false }) as { kind?: string; id?: string };
  const forced = readState();
  const hit = findDetail(kind, id);
  const [openC, setOpenC] = useState<Record<string, boolean>>({ sum: true, flags: true, tl: true, act: true, todo: true, info: true, steps: true, ppl: true, links: true });
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [allAct, setAllAct] = useState(false);
  const [menu, setMenu] = useState(false);
  const tog = (k: string) => () => setOpenC((o) => ({ ...o, [k]: !o[k] }));
  const noWrite = (m?: string) => toast({ msg: m ? `${m} · chưa mở ghi ở bản này` : 'Chưa mở ghi ở bản này' });
  const back = (tab = hit ? TAB_OF[hit.kind] : 'mine') => void navigate({ to: '/ezy/tasks', search: { tab } as never });

  const state: ScreenState = forced ?? (hit ? 'ready' : 'empty');
  const v = hit ? buildDetail(hit.kind, hit.id, { done: !!done.do, ok: !!done.ap }) : null;
  const openN = v ? v.todos.filter((x) => !x.done && !done[x.id]).length : 0;
  const acts = v?.acts ?? [];

  const primary = () => {
    if (!v) return;
    if (v.primary.kind === 'open') toast({ msg: `${v.primary.label} · mở ở vùng liên quan` });
    else if (v.primary.kind === 'deleg') back('deleg');
    else noWrite(v.primary.label);
  };
  const goLink = (to: string, label?: string) => {
    if (to === 'list') back('mine'); else if (to === 'appr') back('appr'); else if (to === 'deleg') back('deleg'); else if (to === 'done') back('done');
    else toast({ msg: `${label ?? 'Mở'} · mở ở vùng liên quan` });
  };

  return (
    <Page kind="detail">
      <PageHeader
        title={v?.title ?? 'Việc & Duyệt'}
        lead={<button type="button" className={s.back} aria-label="Về danh sách việc" title="Về danh sách việc" onClick={() => back()}><IconChevronLeft size={20} stroke={2} aria-hidden="true" /></button>}
        meta={v ? <>{v.chips.slice(0, 2).map((c) => <span key={c.k} className={`${s.note} ${CHIP[c.tone]}`}>{c.k}</span>)}</> : undefined}
        actions={
          <>
            <HeaderTool label="Thông báo · 5 chưa đọc" icon={IconBell} n={5} onClick={() => toast({ msg: '5 thông báo chưa đọc' })} />
            <span className={t.menuWrap}>
              <HeaderTool label="Thao tác khác" icon={IconDots} active={menu} onClick={() => setMenu((x) => !x)} />
              {menu && v && (
                <div role="menu" aria-label="Thao tác khác" className={t.menu}>
                  {['Báo vắng hôm nay', 'Thêm việc ghi nhớ', ...v.menu].map((m) => (
                    <button key={m} type="button" role="menuitem" onClick={() => { setMenu(false); noWrite(m); }}>{m}</button>
                  ))}
                </div>
              )}
            </span>
            {v && v.primary.kind !== 'none' && <Button variant="primary" onClick={primary}>{v.primary.label}</Button>}
          </>
        }
      />
      <StateView
        state={state}
        title={state === 'empty' ? 'Không tìm thấy việc / phiếu' : undefined}
        desc={state === 'empty' ? 'Việc hoặc phiếu này không còn trong danh sách.' : undefined}
        action={state === 'error' ? <Button onClick={() => location.reload()}>{tr('tasks.retry')}</Button> : state === 'empty' ? <Button onClick={() => back('mine')}>{tr('tasks.backToList')}</Button> : undefined}
        skeleton={<Skeleton rows={10} />}
      >
        {v && (
          <div className={s.grid}>
            <div className={s.col}>
              <Card title="Tóm tắt" tip={`${v.rule}`} icon={<IconListDetails size={18} stroke={1.9} />} open={!!openC.sum} onToggle={tog('sum')}>
                <div className={t.kicker}>{v.kicker}</div>
                <div className={t.sumT}>{v.title}</div>
                {v.chips.length > 2 && <div className={t.sumChips}>{v.chips.slice(2).map((c) => <span key={c.k} className={`${s.note} ${CHIP[c.tone]}`}>{c.k}</span>)}</div>}
                {v.gate && <div className={`${t.gate} ${GATE[v.gate.tone]}`}><b>{v.gate.k}</b><div>{v.gate.v}</div></div>}
              </Card>
              {v.isAp && (
                <Card title="Nội dung phiếu" tip="Cờ trên phiếu (vượt hạn mức, trùng người duyệt, bỏ qua bước) và ghi chú của người đề nghị. Phiếu có cờ phải mở từng phiếu, không duyệt hàng loạt." icon={<IconShieldCheck size={18} stroke={1.9} />} open={!!openC.flags} onToggle={tog('flags')}>
                  {v.flags.map((f) => (
                    <div key={f.k} className={t.flag} title={f.tip}><span className={`${s.note} ${f.tone === 'warn' ? t.cWarn : t.cPri}`}>{f.k}</span><span>{f.tip}</span></div>
                  ))}
                  <div className={t.noteRow}><span>{tr('tasks.note')}</span><span>{v.note}</span></div>
                </Card>
              )}
              {!v.isAp && v.timeline.length > 0 && (
                <Card title="Lịch sử" tip="Sinh tự động, gia hạn +4h và đường leo thang khi quá hạn." icon={<IconHistory size={18} stroke={1.9} />} open={!!openC.tl} onToggle={tog('tl')}>
                  {v.timeline.map((x, i) => (
                    <div key={x.k} className={t.tl}>
                      <div className={t.tlRail}><span className={t.tlDot} data-st={x.st} />{i < v.timeline.length - 1 && <span className={t.tlLine} />}</div>
                      <div className={t.tlBody} data-st={x.st}><div>{x.k}</div><div>{x.v}</div></div>
                    </div>
                  ))}
                </Card>
              )}
              <Card title="Hoạt động" tip="Nhật ký của việc / phiếu: sinh, gia hạn +4h, chờ bên ngoài, duyệt / từ chối, đóng. KPI đúng / trễ hạn tính cho người xử lý thực tế." icon={<IconHistory size={18} stroke={1.9} />} extra={<span className={s.cnt}>{acts.length} mục</span>} open={!!openC.act} onToggle={tog('act')}>
                {(allAct ? acts : acts.slice(0, 5)).map((a) => (
                  <div key={a.k} className={s.actRow}>
                    <span className={s.avS} aria-hidden="true">{ini(a.who)}</span>
                    <span className={s.actTx}><span title={a.k}>{a.k}</span><span>{a.who} · {a.when}</span></span>
                    <span className={s.kind}>{a.kind}</span>
                  </div>
                ))}
                {acts.length > 5 && <button type="button" className={d.more} onClick={() => setAllAct((x) => !x)}>{allAct ? 'Thu gọn' : `Xem thêm ${acts.length - 5}`}</button>}
              </Card>
            </div>
            <div className={s.col}>
              <Card title="Việc cần làm" tip="Việc sinh từ việc / phiếu này. Tick để đánh dấu xong." icon={<IconChecklist size={18} stroke={1.9} />} extra={openN ? <span className={s.badge}>{openN}</span> : undefined} open={!!openC.todo} onToggle={tog('todo')}>
                {v.todos.map((x) => {
                  const dn = !!x.done || !!done[x.id];
                  return (
                    <div key={x.id} className={s.todo} style={dn ? { opacity: 0.55 } : undefined}>
                      <button type="button" className={s.chk} role="checkbox" aria-checked={dn} aria-label={`Xong: ${x.k}`} onClick={() => setDone((m) => ({ ...m, [x.id]: !m[x.id] }))}><span>{dn ? '✓' : ''}</span></button>
                      <span className={s.todoTx}>
                        <span title={x.k} style={dn ? { textDecoration: 'line-through' } : undefined}>{x.k}</span>
                        <span><span className={s.avT} aria-hidden="true">{ini(x.who)}</span><span>{x.who} ·</span><span className={x.tone === 'err' ? d.vErr : x.tone === 'warn' ? d.vWarn : undefined}>{x.due}</span></span>
                      </span>
                      {x.act && !dn && <button type="button" className={s.act} onClick={() => (x.id === 'ap' ? setDone((m) => ({ ...m, ap: true })) : x.id === 'do' ? setDone((m) => ({ ...m, do: true })) : noWrite(x.act))}>{x.act}</button>}
                    </div>
                  );
                })}
                {!v.todos.length && <div className={s.none}>{tr('tasks.noTodo')}</div>}
              </Card>
              <Card title="Thông tin chính" tip="Đối tượng, hạn, người được giao và điều kiện sinh / tự đóng của việc." icon={<IconFileText size={18} stroke={1.9} />} open={!!openC.info} onToggle={tog('info')}>
                {v.info.map((x) => (
                  <div key={x.k} className={s.info}>
                    <span>{x.k}</span>
                    {x.href || x.tone === 'link'
                      ? <button type="button" className={d.ln} style={{ flexGrow: 1 }} onClick={() => toast({ msg: `Mở ${x.v} · mở ở vùng liên quan` })}>{x.v} ↗</button>
                      : <span className={x.tone === 'err' ? d.vErr : undefined}>{x.v}</span>}
                    {x.k === 'Đối tượng' && <button type="button" className={s.copy} aria-label={`Chép ${x.k}`} title="Chép" onClick={() => { void navigator.clipboard?.writeText(x.v); toast({ msg: `Đã chép ${x.k}` }); }}><IconCopy size={16} stroke={1.9} aria-hidden="true" /></button>}
                  </div>
                ))}
              </Card>
              <Card title="Các bước duyệt theo ghế" tip="Người xử lý theo ghế đang giữ (TblOrgAssign). Một người không ký hai bước của cùng một phiếu." icon={<IconRoute size={18} stroke={1.9} />} extra={<span className={s.cnt}>{v.stepsK}</span>} open={!!openC.steps} onToggle={tog('steps')}>
                <div className={d.stepW}>
                  {v.steps.map((x, i) => (
                    <div key={`${x.seat}${i}`} className={d.step}>
                      <div className={d.stepRail}>
                        <span className={`${d.stepN} ${d[x.st] ?? ''}`}>{x.st === 'done' ? '✓' : x.st === 'rej' ? '×' : i + 1}</span>
                        {i < v.steps.length - 1 && <span className={d.stepLine} />}
                      </div>
                      <div className={d.stepBody}>
                        <span>{x.seat}</span>
                        <span className={d.stepWho}><span className={s.avT} aria-hidden="true">{ini(x.who)}</span><b>{x.who}</b><span className={`${d.stepSt} ${d[x.st] ?? ''}`}>{x.stK}</span></span>
                        <span className={d.stepSub}>{x.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
              <Card title="Người phụ trách" tip="Avatar lấy từ TblStaff.ImageProfile (HanaAvatar), chưa có ảnh thì hiện chữ cái. Người duyệt hiện theo ghế (TblOrgAssign)." icon={<IconUserShield size={18} stroke={1.9} />} open={!!openC.ppl} onToggle={tog('ppl')}>
                {v.people.map((u) => (
                  <div key={u.u} className={s.ppl}>
                    <span className={s.avM} aria-hidden="true">{ini(u.u)}</span>
                    <span className={s.pplTx}><b>{u.u}</b><span>{u.role}</span></span>
                    {u.hei && <a className={s.hei} href="/hei/" target="_blank" rel="noopener" aria-label={`Nhắn ${u.u} trong HANA Hei`} title="Nhắn trong HANA Hei"><IconMessageCircle size={18} stroke={1.9} aria-hidden="true" /></a>}
                  </div>
                ))}
              </Card>
              <Card title="Liên kết" tip="Lối sang màn liên quan. Mở trong cùng HANA Ezy, giữ ngữ cảnh bản ghi." icon={<IconLink size={18} stroke={1.9} />} open={!!openC.links} onToggle={tog('links')}>
                {v.links.map((l) => (
                  <button key={l.k} type="button" className={s.link} onClick={() => goLink(l.to, l.label)}>
                    <IconFileText size={18} stroke={1.9} aria-hidden="true" />
                    <span><span>{l.k}</span><span>{l.sub}</span></span>
                    <IconChevronRight size={16} aria-hidden="true" />
                  </button>
                ))}
              </Card>
            </div>
          </div>
        )}
      </StateView>
    </Page>
  );
}
